import * as React from "react";

import { AccessReportsService } from "@app/access-reports";
import {
  AccessReportProviderSetCopyCollectionModel,
  AccessReportProviderSetCopyModel,
  AccessReportProviderSetCopyResponseModel,
  AccessReportsProviderSetCopyDashboardResponseModel,
} from "@app/access-reports/admin/";
import { Message, MessageType, NotificationContextProps, withNotificationContext } from "@app/notifications";
import { AccessReportMaintJobStatusChangeNotificationMessagePayload } from "@app/notifications/message";
import { QESButton } from "@app/shared/qes-standards";
import { ColumnConfig, SortDirections, Waffles } from "@app/shared/waffles";
import { cloneDeep } from "lodash";
import { SortDirection } from "@app/shared/griddle/enumerators";
import { JSX } from "react";

type CacheProviderSetsProps = NotificationContextProps & {
  hideSidePanel: () => void;
}

interface CacheProviderSetsState {
  loading: boolean;
  offset: number;
  providerSetCopies: AccessReportProviderSetCopyModel[];
  providerSetCopyResponses: AccessReportProviderSetCopyResponseModel[];
  selectedProviderSetCopies: AccessReportProviderSetCopyModel[];
  sortDirection: SortDirection;
  sortedColumn: keyof CacheProviderSetsWaffleToppings;
  totalCount: number;
}

class CacheProviderSetsWaffleToppings {
  public key: string;
  public providerSetName: string;
  public status: string;

  constructor(
    key: string,
    providerSetName: string,
    status: string,
  ) {
    /* eslint-disable sort-keys */
    this.key = key;
    this.providerSetName = providerSetName;
    this.status = status;
    /* eslint-enable sort-keys */
  }
}

class CacheProviderSets extends React.Component<CacheProviderSetsProps, CacheProviderSetsState> {
  private readonly accessReportsService = new AccessReportsService();
  private readonly batchCount: number = 100;

  constructor(props: CacheProviderSetsProps) {
    super(props);

    this.state = {
      loading: true,
      offset: 0,
      providerSetCopies: [],
      providerSetCopyResponses: [],
      selectedProviderSetCopies: [],
      sortDirection: SortDirection.Unknown,
      sortedColumn: null,
      totalCount: 0,
    };
  }

  public async componentDidMount() {
    this.props.subscribe(this.handleNotifications);

    this.getProviderSets();
  }

  public componentWillUnmount() {
    this.props.unsubscribe(this.handleNotifications);
  }

  public render() {
    return (
      <React.Fragment>
        <div className="row">
          <div className="col-12">
            <Waffles<CacheProviderSetsWaffleToppings>
              bulkActionBar={this.buildBulkActionBar()}
              columns={this.getColumnConfig()}
              customSort={this.customSort}
              data={this.buildWaffleToppings(this.state.providerSetCopies) || []}
              fullWidth={true}
              handleCheckboxChange={this.handleCheckboxChange}
              passThroughSorting={true}
              selectedKeys={this.state.selectedProviderSetCopies.map((sr: AccessReportProviderSetCopyModel) => sr.providerSetCopyId.toString())}
              showCheckboxes={true}
              testId="access-reports-admin__cache-provider-sets--table"
            />
          </div>
        </div>

        {(this.state.providerSetCopies.length < this.state.totalCount) && (
          <div className="row">
            <div className="col-12">
              <div className="text-center">
                <QESButton
                  qesTestId="access-reports-admin__cache-provider-sets--load-more"
                  className="mt-3"
                  onClick={() => this.getProviderSets()}
                >
                  Load More
                </QESButton>
              </div>
            </div>
          </div>
        )}
      </React.Fragment>
    );
  }

  private buildBulkActionBar = (): JSX.Element => {
    return (
      <QESButton
        className="text-info"
        disabled={!this.state.selectedProviderSetCopies.length}
        onClick={() => this.onCacheProviderSetsClick()}
        qesTestId="access-reports-admin__cache-provider-sets--cache"
        qesType="secondary"
      >
        Cache Selected Providers
      </QESButton>
    );
  }

  private buildWaffleToppings = (providerSets: AccessReportProviderSetCopyModel[]): CacheProviderSetsWaffleToppings[] => {
    const waffleToppings: CacheProviderSetsWaffleToppings[] = providerSets.map((p) => {
      const key: string = p.providerSetCopyId.toString();

      const providerSetName: string = p.providerSetName;

      const status: string = this.getStatus(p);

      return new CacheProviderSetsWaffleToppings(key, providerSetName, status);
    });

    return waffleToppings;
  }

  private getColumnConfig = (): ColumnConfig => {
    const columns: ColumnConfig = {
      providerSetName: {
        displayText: "Provider Set Name",
        noResize: true,
        testId: "providerSetName",
      },
      status: {
        displayText: "Status",
        noResize: true,
        testId: "status",
      },
    };

    return columns;
  }

  private customSort = (records: CacheProviderSetsWaffleToppings[], column: keyof CacheProviderSetsWaffleToppings, direction: string): CacheProviderSetsWaffleToppings[] => {
    let sortDirection: SortDirection = null;
    switch (direction) {
      case SortDirections.Asc:
        sortDirection = SortDirection.Ascending;
        break;
      case SortDirections.Desc:
        sortDirection = SortDirection.Descending;
        break;
      case SortDirections.None:
        sortDirection = SortDirection.None;
        break;

      default:
        sortDirection = SortDirection.Unknown;
    }

    this.getProviderSets(true, column, sortDirection);

    return records;
  }

  private getProviderSets = async (
    isSorting = false,
    sortedColumn: keyof CacheProviderSetsWaffleToppings = this.state.sortedColumn,
    sortDirection: SortDirection = this.state.sortDirection,
  ): Promise<void> => {
    let providerSetCopies: AccessReportProviderSetCopyModel[] = this.state.providerSetCopies;
    let batchCount: number = this.batchCount;
    let offset: number = this.state.offset;
    let totalCount: number = this.state.totalCount;

    if (isSorting && !!sortedColumn && !!sortDirection) {
      // if sorting, return the same amount of records that are currently displayed,
      // without the need for any offsets
      batchCount = this.state.providerSetCopies.length;
      offset = 0;
    }

    if (isSorting && (sortDirection === SortDirection.None || sortDirection === SortDirection.Unknown)) {
      // this occurs when we're undoing a sort
      sortedColumn = null;
      sortDirection = SortDirection.Unknown;
    }

    const providerSetCopyResponseModel: AccessReportsProviderSetCopyDashboardResponseModel = await this.accessReportsService.getProviderSets(batchCount, offset, sortDirection, sortedColumn);

    if (providerSetCopyResponseModel) {
      totalCount = providerSetCopyResponseModel.totalCount;

      if (isSorting) {
        // sorting, meaning we should just replace the old records with the new ones
        providerSetCopies = providerSetCopyResponseModel.providerSetCopies;
      } else {
        // acquiring more records (either on load, or on load more click),
        // so the new batch needs to be appended
        providerSetCopies = providerSetCopies.concat(providerSetCopyResponseModel.providerSetCopies);
      }
    }

    this.setState({
      loading: false,
      offset: offset + batchCount,
      providerSetCopies,
      totalCount,
    });
  }

  private getStatus = (providerSetCopy: AccessReportProviderSetCopyModel) => {
    const copiedResponse: AccessReportProviderSetCopyResponseModel = this.state.providerSetCopyResponses.find(x => x.providerSetCopyId === providerSetCopy.providerSetCopyId);

    if (!!copiedResponse?.errorMessage) {
      return copiedResponse.errorMessage;
    } else {
      return providerSetCopy.status;
    }
  }

  private handleCheckboxChange = (selectedKeys: string[]): void => {
    const selectedProviderSetCopies: AccessReportProviderSetCopyModel[] = this.state.providerSetCopies.filter((x) => selectedKeys.includes(`${x.providerSetCopyId.toString()}`));

    this.setState({
      selectedProviderSetCopies,
    });
  }

  private handleNotifications = async (message: Message): Promise<void> => {
    if (message?.notificationType === MessageType.AccessReportsProviderSetCopyStatusChange) {
      const payload: AccessReportMaintJobStatusChangeNotificationMessagePayload = message.payload as AccessReportMaintJobStatusChangeNotificationMessagePayload;

      if (!!payload) {
        const providerSetCopies: AccessReportProviderSetCopyModel[] = cloneDeep(this.state.providerSetCopies);
        const providerSetCopy: AccessReportProviderSetCopyModel = providerSetCopies.find(x => x.providerSetCopyId === payload.providerSetCopyId);

        if (!!providerSetCopy) {
          providerSetCopy.status = payload.jobStatus;
        }

        this.setState({
          providerSetCopies,
        });
      }
    }
  }

  private onCacheProviderSetsClick = async (): Promise<void> => {
    const providerSetCopyCollectionModel: AccessReportProviderSetCopyCollectionModel = {
      providerSetCopyIds: this.state.selectedProviderSetCopies.map((x: AccessReportProviderSetCopyModel) => x.providerSetCopyId),
    };

    const providerSetCopyResponses: AccessReportProviderSetCopyResponseModel[] = await this.accessReportsService.copyProviderSets(providerSetCopyCollectionModel);

    if (providerSetCopyResponses.length) {
      const providerSetCopies = cloneDeep(this.state.providerSetCopies);
      for (const providerSetCopy of providerSetCopies) {
        const response = providerSetCopyResponses.find(x => x.providerSetCopyId === providerSetCopy.providerSetCopyId);

        if (!!response) {
          providerSetCopy.qesJobId = response.qesJobId;
        }
      }

      this.setState({
        providerSetCopies,
        providerSetCopyResponses,
        selectedProviderSetCopies: [],
      });
    }
  }
}

const WrappedCacheProviderSets = withNotificationContext(CacheProviderSets);

export default WrappedCacheProviderSets as React.ComponentClass<CacheProviderSetsProps>;

